import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

/**
 * Read-side view of parcel verification photos attached to a single
 * fulfillment group. Renders a thumbnail strip; clicking any thumb
 * opens a full-screen lightbox with the same keyboard nav
 * (Esc/Left/Right) the capture panel's lightbox already uses.
 *
 * Returns null when:
 *   - The FG id is missing (multi-select states, loading races).
 *   - The fetch returned zero photos (avoids rendering an empty
 *     "Photos" section on FGs that never used the feature).
 *
 * Loading and error states render inline as a tiny status line —
 * the slideout already has loading affordances above us and we
 * don't want to compete.
 */
export const ParcelPhotosViewer = memo(function ParcelPhotosViewer({
  fulfillmentGroupId,
}: {
  fulfillmentGroupId: string | null | undefined;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  // Skip the fetch entirely when the merchant hasn't opted into the
  // feature. Saves a useless round trip per FG mount on every
  // slideout / detail-page open across the whole non-opted-in
  // population. We still mount the viewer (so toggling the feature
  // on doesn't require any tree restructure); the early `null`
  // return just makes it a no-op.
  const parcelPhotosEnabled =
    team?.settings.outboundLabels.verifyParcelPhotos?.enabled ?? false;
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const photosLoad = useLoad(async () => {
    if (!fulfillmentGroupId || !parcelPhotosEnabled) {
      return { photos: [] };
    }
    return await rpcClient.getParcelPhotos({ fulfillmentGroupId });
  }, [rpcClient, fulfillmentGroupId, parcelPhotosEnabled]);

  const photos = photosLoad.value?.photos ?? [];

  // Lightbox keyboard nav — same shape as the capture panel's
  // lightbox so the operator's muscle memory carries over.
  useEffect(() => {
    if (lightboxIdx == null) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setLightboxIdx(null);
          break;
        case "ArrowRight":
          e.preventDefault();
          setLightboxIdx((i) =>
            i == null ? null : Math.min(i + 1, photos.length - 1),
          );
          break;
        case "ArrowLeft":
          e.preventDefault();
          setLightboxIdx((i) => (i == null ? null : Math.max(i - 1, 0)));
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, photos.length]);

  if (photosLoad.pending) {
    return (
      <Text fontSize="xs" textColor="tertiary">
        Loading photos…
      </Text>
    );
  }
  if (photosLoad.error) {
    return (
      <Text fontSize="xs" textColor="error">
        Failed to load parcel photos.
      </Text>
    );
  }
  if (photos.length === 0) {
    return null;
  }

  const lightboxPhoto = lightboxIdx != null ? photos[lightboxIdx] : undefined;

  return (
    <>
      <Flex gap="sm" style={{ flexWrap: "wrap" }}>
        {photos.map((photo, i) => (
          <button
            aria-label={`Open parcel photo ${i + 1} at full size`}
            key={photo.photoId.toString()}
            onClick={() => setLightboxIdx(i)}
            style={{
              padding: "var(--redo-spacing-none)",
              background: "none",
              border: "none",
              cursor: "zoom-in",
              outline: "none",
              borderRadius: "var(--border-radius-sm)",
            }}
            type="button"
          >
            <img
              alt={`Parcel photo ${i + 1}`}
              src={photo.url}
              style={{
                width: 88,
                height: 88,
                objectFit: "cover",
                borderRadius: "var(--border-radius-sm)",
                border: "1px solid var(--redo-colors-border-border-secondary)",
                display: "block",
              }}
            />
          </button>
        ))}
      </Flex>
      {lightboxPhoto && typeof document !== "undefined"
        ? createPortal(
            <div
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setLightboxIdx(null);
                }
              }}
              style={{
                position: "fixed",
                inset: 0,
                // Same max-int z-index as the capture panel's lightbox
                // so the FG slideout / detail page header chrome
                // doesn't peek through.
                zIndex: 2_147_483_647,
                background: "rgba(0, 0, 0, 0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "var(--redo-spacing-3xl)",
              }}
            >
              <button
                aria-label="Close preview"
                onClick={() => setLightboxIdx(null)}
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  background: "rgba(255, 255, 255, 0.12)",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  padding: "var(--redo-spacing-md)",
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  outline: "none",
                }}
                type="button"
              >
                <XCloseIcon style={{ width: 18, height: 18 }} />
              </button>
              <img
                alt=""
                src={lightboxPhoto.url}
                style={{
                  maxWidth: "92vw",
                  maxHeight: "88vh",
                  borderRadius: "var(--border-radius-lg)",
                  boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 12,
                }}
              >
                {lightboxIdx! + 1} / {photos.length}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
});
